import React, { useEffect, useState } from 'react';
import { login } from '../services/auth.service';
import { useAuth } from '../contexts/AuthContext';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import Button from '../components/Button';
import { getUrlParams, removeUrlParam } from '../services/parameter.service';
import GoogleSignIn from '../components/GoogleSignIn';
import { isValidReturnUrl } from '../helpers/urls';
import Feedback from '../components/Feedback';

const Login: React.FC = () => {
  const [emailAddress, setEmailAddress] = useState('');
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(true);
  const [message, setMessage] = useState('');
  const [errorMessages, setErrorMessage] = useState([] as string[]);
  const [loading, setLoading] = useState(false);

  const location = useLocation();
  const navigate = useNavigate();
  const from = (location.state as { from?: Location })?.from?.pathname;

  const {
    passwordChanged,
    emailChanged,
    deletedSelf,
    returnUrl,
    forwardUrl,
    failed
  } = getUrlParams();

  const { isLoggedIn } = useAuth();

  const redirectUrl = returnUrl ?? from;

  useEffect(() => {
    if (passwordChanged) {
      setMessage('Password changed. Please log in again.');
      removeUrlParam('passwordChanged');
    }
    if (emailChanged) {
      setMessage(
        `Email address changed to ${emailChanged}. Please log in again.`
      );
      setEmailAddress(emailChanged);
      removeUrlParam('emailChanged');
    }
    if (deletedSelf) {
      setMessage('Your account has been deleted');
      removeUrlParam('deletedSelf');
    }
    if (redirectUrl) {
      setErrorMessage([`You don't have access to this page.  Please log in.`]);
    }
    if (failed) {
      setErrorMessage(['There was a problem logging in.  Please try again.']);
      removeUrlParam('failed');
    }
  }, []);

  const handleLogin = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await login(emailAddress, password, rememberMe);
      if (response.success) {
        if (response.data?.alreadyLoggedIn) {
          window.location.href = '/dashboard';
          return;
        }
        if (response.data?.requiresTwoFactor) {
          const redirect = redirectUrl
            ? `/Account/LoginMFA?rememberMe=${rememberMe}&returnUrl=${redirectUrl}`
            : forwardUrl
              ? `/Account/LoginMFA?rememberMe=${rememberMe}&forwardUrl=${forwardUrl}`
              : `/Account/LoginMFA?rememberMe=${rememberMe}`;

          navigate(redirect);
          return;
        }
        if (redirectUrl && isValidReturnUrl(redirectUrl)) {
          window.location.href = redirectUrl;
        } else if (forwardUrl && isValidReturnUrl(forwardUrl)) {
          window.location.href = forwardUrl;
        } else {
          window.location.href = '/dashboard';
        }
      } else {
        setMessage('');
        setErrorMessage(response.errors);
        setLoading(false);
      }
    } catch (error) {
      setMessage('');
      setErrorMessage(['Invalid credentials']);
      setLoading(false);
    }
  };

  if (isLoggedIn && !redirectUrl && !forwardUrl) {
    return <></>;
  }

  return (
    <div>
      <h2>Welcome back</h2>
      <form onSubmit={handleLogin} className="login-form">
        <input
          type="text"
          id="username"
          placeholder="Email Address"
          required
          value={emailAddress}
          onChange={(e) => setEmailAddress(e.target.value)}
        />
        <input
          type="password"
          id="password"
          placeholder="Password"
          required
          value={password}
          onChange={(e) => setPassword(e.target.value)}
        />
        <div className="checkbox-container">
          <input
            type="checkbox"
            id="rememberMe"
            checked={rememberMe}
            onChange={(e) => setRememberMe(e.target.checked)}
          />
          <label htmlFor="rememberMe">Remember Me</label>
        </div>
        <div className="button-container">
          <Button isLoading={loading} isSubmit={true}>
            Login
          </Button>
          <Button
            type="Outline"
            className="cancel-button"
            onClick={() => navigate('/')}
          >
            Cancel
          </Button>
        </div>
        <Feedback message={message} type="success" />
        <Feedback messages={errorMessages} type="error" />
        <Link to="/Account/ForgottenPassword">
          Can&apos;t remember your password?
        </Link>
        <div className="external">
          <GoogleSignIn setErrors={setErrorMessage} />
        </div>
        <div className="links">
          <Link to="/Policies/terms-of-use">Terms of Use</Link>|
          <Link to="/Policies/privacy-policy">Privacy Policy</Link>
        </div>
      </form>
    </div>
  );
};

export default Login;
